import React, { useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { SavedListing } from '@/types/residents/marketplaceListing';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  SavedListingCard,
  ProfileNavDropdown,
  MarketplaceEmptyState,
} from '@/components/residents/marketplace';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
} from '@/utils/marketplace';
import { useGetSavedItemsQuery, useToggleSavedItemMutation } from '@/store/api';

type SavedListingsNavigationProp = StackNavigationProp<RootStackParamList, 'SavedListings'>;
type SavedListingsRouteProp = RouteProp<RootStackParamList, 'SavedListings'>;

export const SavedListingsScreen: React.FC = () => {
  const navigation = useNavigation<SavedListingsNavigationProp>();
  const route = useRoute<SavedListingsRouteProp>();
  const { layoutMode, isWeb } = useResponsive();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Fetch saved items from API
  const { 
    data: savedListings = [], 
    isLoading,
    refetch,
  } = useGetSavedItemsQuery();

  // Toggle saved mutation
  const [toggleSavedItem] = useToggleSavedItemMutation();

  // Handle unsaved action from detail screen - refetch instead of local state update
  useFocusEffect(
    useCallback(() => {
      const params = route.params as { unsavedId?: string } | undefined;
      if (params?.unsavedId) {
        // Refetch to get updated list
        refetch();
        // Clear the params after handling
        navigation.setParams({ unsavedId: undefined } as any);
      }
    }, [route.params, refetch])
  );

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleListingPress = (listing: SavedListing) => {
    navigation.navigate('SavedItemDetail', { listing });
  };

  const handleBookmarkPress = async (listing: SavedListing) => {
    try {
      await toggleSavedItem(listing.id).unwrap();
    } catch (error) {
      console.error('Failed to unsave item:', error);
    }
  };

  return (
    <MarketplaceScreenLayout navigatorTitle="Saved">
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: contentPadding },
          !isMobile && styles.contentLarge,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Responsive content wrapper */}
        <View style={[
          styles.contentWrapper,
          maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
        ]}>
          {/* Back Navigation */}
          <BreadcrumbNavigation 
            onHomePress={handleGoToMarketplace}
            onBackPress={handleGoBack}
          />

          {/* Profile Navigation Dropdown */}
          <ProfileNavDropdown activeOption="saved" />

          {/* Saved Listings */}
          <View style={styles.listingsContainer}>
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
              </View>
            ) : savedListings.length > 0 ? (
              savedListings.map((listing) => (
                <SavedListingCard
                  key={listing.id}
                  listing={listing}
                  onPress={() => handleListingPress(listing)}
                  onBookmarkPress={() => handleBookmarkPress(listing)}
                />
              ))
            ) : (
              <MarketplaceEmptyState
                title="No saved items"
                subtitle="Items you save will appear here"
              />
            )}
          </View>
        </View>
      </ScrollView>
    </MarketplaceScreenLayout>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 24,
  },
  contentLarge: {
    gap: 32,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 24,
  },
  listingsContainer: {
    gap: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
});
